import { useState } from "react";
import ConfirmDialog from "@/pages/branch/orders/components/ConfirmDialog";

interface EmergencyClosureSectionProps {
  active: boolean;
  reason: string;
  onToggle: (active: boolean) => void;
  onChangeReason: (reason: string) => void;
}

export default function EmergencyClosureSection({
  active,
  reason,
  onToggle,
  onChangeReason,
}: EmergencyClosureSectionProps) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");

  const handleRequestClose = () => {
    if (reason.trim().length < 3) {
      setError("يجب كتابة سبب الإغلاق قبل تنفيذ الإغلاق الطارئ");
      return;
    }
    setError("");
    setConfirming(true);
  };

  const handleConfirmClose = () => {
    setConfirming(false);
    onToggle(true);
  };

  const handleDisable = () => {
    setError("");
    onToggle(false);
  };

  const handleReason = (value: string) => {
    setError("");
    onChangeReason(value);
  };

  return (
    <section
      className={`rounded-card border overflow-hidden transition-colors ${
        active ? "border-red-200 bg-red-50" : "border-background-200 bg-background-50"
      }`}
    >
      <div
        className={`flex items-center gap-3 px-5 py-4 border-b ${
          active ? "border-red-200" : "border-background-200"
        }`}
      >
        <div className="w-9 h-9 rounded-btn flex items-center justify-center bg-red-100 text-red-500">
          <i className="ri-alarm-warning-line text-xl"></i>
        </div>
        <div>
          <h2 className="text-base font-bold text-foreground-900">الإغلاق الطارئ</h2>
          <p className="text-xs text-foreground-500 mt-0.5">
            إيقاف استقبال الطلبات الجديدة مؤقتًا في حالات الطوارئ
          </p>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {active ? (
          <div className="flex items-center gap-3 px-4 py-3 rounded-btn bg-red-100 border border-red-200 animate-fade-slide-down">
            <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white shrink-0 animate-pulse-ring">
              <i className="ri-shield-flash-line text-base"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-red-700">الفرع مغلق مؤقتًا الآن</p>
              <p className="text-xs text-red-600 mt-0.5">
                لن يستقبل التطبيق أي طلبات جديدة حتى إعادة الفتح
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 px-4 py-3 rounded-btn bg-secondary-50 border border-secondary-200">
            <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white shrink-0">
              <i className="ri-check-line text-base"></i>
            </div>
            <p className="text-sm text-secondary-700">
              الفرع يعمل بشكل طبيعي ويستقبل الطلبات
            </p>
          </div>
        )}

        <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-btn bg-accent-50 border border-accent-200">
          <i className="ri-list-check-2 text-base text-accent-600 mt-0.5 shrink-0"></i>
          <p className="text-xs text-accent-800 leading-relaxed">
            الطلبات النشطة الحالية لن تتأثر بالإغلاق الطارئ، وستستمر في استكمال
            مراحلها بشكل طبيعي.
          </p>
        </div>

        <div>
          <label className="block mb-2 text-sm font-semibold text-foreground-700">
            سبب الإغلاق (يظهر للعملاء)
          </label>
          <textarea
            value={reason}
            onChange={(e) => handleReason(e.target.value.slice(0, 300))}
            placeholder="مثال: صيانة طارئة، انقطاع كهرباء، ظرف خاص..."
            rows={2}
            className={`w-full rounded-btn border bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 px-4 py-3 outline-none resize-none transition-all ${
              error
                ? "border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100"
                : "border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
            }`}
          ></textarea>
          <div className="flex items-center justify-between mt-1.5 gap-3">
            <p className="text-xs text-foreground-400">{reason.length}/300 حرف</p>
            {error ? (
              <p className="text-xs text-red-600 flex items-center gap-1">
                <i className="ri-error-warning-line text-sm"></i>
                {error}
              </p>
            ) : null}
          </div>
        </div>

        {active ? (
          <button
            type="button"
            onClick={handleDisable}
            className="w-full h-12 rounded-btn bg-primary-500 text-white font-bold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="inline-flex items-center gap-2">
              <i className="ri-door-open-line text-lg"></i>
              إعادة فتح الفرع الآن
            </span>
          </button>
        ) : (
          <button
            type="button"
            onClick={handleRequestClose}
            className="w-full h-12 rounded-btn font-bold text-sm transition-colors cursor-pointer whitespace-nowrap bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
          >
            <span className="inline-flex items-center gap-2">
              <i className="ri-door-lock-line text-lg"></i>
              إغلاق الفرع مؤقتًا
            </span>
          </button>
        )}
      </div>

      {confirming ? (
        <ConfirmDialog
          title="تأكيد الإغلاق الطارئ"
          message="سيتم إيقاف استقبال الطلبات الجديدة من التطبيق فورًا، مع استمرار الطلبات النشطة بشكل طبيعي. هل تريد المتابعة؟"
          confirmLabel="تأكيد الإغلاق"
          cancelLabel="رجوع"
          tone="danger"
          onConfirm={handleConfirmClose}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </section>
  );
}